import { useState } from "react";
import { useQuery } from "@tanstack/react-query";

import {
  Box,
  Flex,
  Link,
  Stack,
  useColorModeValue,
  useToast,
  Text,
  Button,
  HStack,
  IconButton,
  InputGroup,
  InputLeftElement,
  Input,
  Select,
  TableContainer,
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
} from "@chakra-ui/react";
import { Link as RouterLink } from "react-router-dom";
import { useConditionalBackground } from "@/contexts/useConditionalBackground";
import { useEnsureAuthenticated } from "@/contexts/ensureAuthenticated";
import { userPermissions } from "@/contexts/userPermissions";
import { updateUserStatus } from "@/services/user.service";
import { User } from "@/models/user.model";
import * as XLSX from "xlsx";
import { FaFileExcel, FaUserPlus } from "react-icons/fa";
import { PermissionEnum } from "@/utils/permissionEnum";
import { CiGrid41, CiSearch } from "react-icons/ci";
import {
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronUpIcon,
} from "@chakra-ui/icons";
import { getCreditPromoters } from "@/services/creditPromoter.service";
import { CreditPromoter } from "@/models/creditPromoter.model";
import { documentFormatter } from "@/utils/documentFormatter";
import { useDebounce } from "@/utils/useDebounce";
import SplitApprovalTab from "@/components/SplitApprovalTab";
import {
  SplitStatusEnum,
  SplitStatusLabels,
} from "@/models/splitStatusEnum.model";

export default function PromotorasTable() {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const { user } = useEnsureAuthenticated();
  const permissions = userPermissions(user);
  const toast = useToast();
  const backgroundStyle = useConditionalBackground();

  const [sortBy, setSortBy] = useState("");
  const [sortDirection, setSortDirection] = useState("asc");

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 500);

  const [splitSearch, setSplitSearch] = useState("");
  const [splitStatusFilter, setSplitStatusFilter] = useState("");

  const [tabIndex, setTabIndex] = useState(0);


  const handleSort = (field: string) => {
    setSortBy(field);
    setSortDirection(
      sortBy === field && sortDirection === "asc" ? "desc" : "asc"
    );
  };

  const fetchPromotoras = async () => getCreditPromoters(page, pageSize, debouncedSearch);

  const staleSeconds = 20;

  const { data, refetch } = useQuery({
    queryKey: ["promotoras", page, pageSize, debouncedSearch],
    queryFn: fetchPromotoras,
    staleTime: staleSeconds * 1000,
  });

  const creditPromotersData = data?.data || [];
  const totalPages = data?.totalPages || 1;

  const headerColor = useColorModeValue("white", "white");
  const rowHoverBg = useColorModeValue("teal.50", "teal.900");
  const rowBorderColor = useColorModeValue("gray.200", "gray.600");

  const handleDeactivateUser = async (user: User) => {
    try {
      if (!user.id) {
        throw new Error("ID do usuário é indefinido.");
      }

      const updatedUser = { id: user.id, enabled: false };
      await updateUserStatus(updatedUser);

      toast({
        title: "Usuário desativado com sucesso!",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      await refetch();
    } catch (error) {
      console.error("Erro ao desativar usuário:", error);
      toast({
        title: "Erro ao desativar usuário",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleDownload = async () => {
    const exportData = creditPromotersData.map(
      (creditPromoter: {
        id?: number;
        document: string;
        legalName: string;
        businessName: string;
      }) => ({
        id: creditPromoter.id,
        document: creditPromoter.document,
        legalName: creditPromoter.legalName,
        businessName: creditPromoter.businessName,
      })
    );

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Users");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    const dataBlob = new Blob([excelBuffer], {
      type: "application/octet-stream",
    });

    const url = window.URL.createObjectURL(dataBlob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "promoters.xlsx";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast({
      title: "Arquivo Excel de promotoras baixado com sucesso.",
      status: "success",
      duration: 3000,
      isClosable: true,
    });
  };

  const TotalusersData = data?.data || [];


  return (
    <Tabs colorScheme="teal" width="100%" index={tabIndex} onChange={(index) => setTabIndex(index)}>
      <Box
        padding={{ base: "2", md: "4" }}
        display="flex"
        flexDirection="column"
        width={"100%"}
      >
        <Box
          {...backgroundStyle}
          borderRadius="md"
          p={{ base: 4, md: 6 }}
          color="white"
          boxShadow="md"
          width="100%"
        >
          <Flex
            flexDirection={{ base: "column", md: "row" }}
            alignItems={{ base: "flex-start", md: "center" }}
            justifyContent="space-between"
            mb={4}
          >
            <Flex alignItems="center" mb={{ base: 4, md: 0 }}>
              <CiGrid41 size="2rem" />
              <Text
                fontSize={{ base: "2xl", md: "4xl" }}
                fontWeight="bold"
                ml={4}
                fontFamily="Lexend Deca"
              >
                Promotoras
              </Text>
              <TabList ml={6} borderBottom="none">
                <Tab
                  fontFamily="Lexend Deca"
                  fontWeight="bold"
                  fontSize={{ base: "lg", md: "xl", lg: "2xl" }}
                  color="whiteAlpha.800"
                  _selected={{ color: "white", borderBottomColor: "white" }}
                >
                  Cadastro
                </Tab>
                <Tab
                  fontFamily="Lexend Deca"
                  fontWeight="bold"
                  fontSize={{ base: "lg", md: "xl", lg: "2xl" }}
                  color="whiteAlpha.800"
                  _selected={{ color: "white", borderBottomColor: "white" }}
                >
                  Aprovação de Split
                </Tab>
              </TabList>
            </Flex>
          </Flex>

          {tabIndex === 0 && (
            <Flex
              flexDirection={{ base: "column", md: "row" }}
              alignItems="flex-end"
              mt={{ base: 4, md: 0 }}
              width="100%"
              justifyContent={{ base: "center", md: "space-between" }}
              gap={4}
            >
              <Flex w="30%" gap={2}>
                <InputGroup>
                  <InputLeftElement mt={1} pointerEvents="none">
                    <CiSearch color="black" size="2rem" />
                  </InputLeftElement>
                  <Input
                    height={{ base: "56px", md: "48px" }}
                    bg={"white"}
                    placeholder="Pesquisar..."
                    variant="filled"
                    type="text"
                    maxLength={30}
                    color="black"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    _focus={{ bg: "white" }}
                    _hover={{ bg: "white" }}
                    fontFamily="Lexend Deca"
                  />
                </InputGroup>
              </Flex>
              <Flex gap={4} alignItems="center">
                {permissions.some(
                  (permission) => permission === PermissionEnum.GRANATECH
                ) && (
                    <Stack
                      direction={{ base: "column", md: "row" }}
                      spacing={4}
                      justifyContent={{ base: "center", md: "end" }}
                    >
                      <Link
                        as={RouterLink}
                        to="new"
                        bg="white"
                        color="black"
                        px={4}
                        py={{ base: 4, md: 2 }}
                        borderRadius="md"
                        fontWeight="bold"
                        _hover={{ bg: "gray.200" }}
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        fontSize={{ base: "lg", md: "md" }}
                        height={{ base: "56px", md: "40px" }}
                        width={{ base: "100%", md: "auto" }}
                        fontFamily="Lexend Deca"
                      >
                        <FaUserPlus
                          style={{ marginRight: "8px", fontSize: "20px" }}
                        />
                        Cadastrar Promotora
                      </Link>
                    </Stack>
                  )}
                <HStack spacing={2} align={"flex-start"}>
                  <IconButton
                    icon={<FaFileExcel />}
                    aria-label="Download Excel"
                    onClick={handleDownload}
                    colorScheme="green"
                  />
                </HStack>
              </Flex>
            </Flex>
          )}
          {tabIndex === 1 && (
            <Flex
              flexDirection={{ base: "column", md: "row" }}
              alignItems={{ base: "stretch", md: "center" }}
              mt={{ base: 4, md: 0 }}
              gap={2}
              w={{ base: "100%", md: "60%" }}
            >
              <InputGroup>
                <InputLeftElement mt={1} pointerEvents="none">
                  <CiSearch color="black" size="2rem" />
                </InputLeftElement>
                <Input
                  height={{ base: "56px", md: "48px" }}
                  bg={"white"}
                  placeholder="Pesquisar..."
                  variant="filled"
                  type="text"
                  maxLength={30}
                  color="black"
                  value={splitSearch}
                  onChange={(e) => setSplitSearch(e.target.value)}
                  _focus={{ bg: "white" }}
                  _hover={{ bg: "white" }}
                  fontFamily="Lexend Deca"
                />
              </InputGroup>
              <Select
                height={{ base: "56px", md: "48px" }}
                bg="white"
                color="black"
                variant="filled"
                value={splitStatusFilter}
                onChange={(e) => setSplitStatusFilter(e.target.value)}
                _focus={{ bg: "white" }}
                _hover={{ bg: "white" }}
                fontFamily="Lexend Deca"
                maxW={{ base: "100%", md: "340px" }}
              >
                <option value="">Todos os status</option>
                {Object.values(SplitStatusEnum).map((status) => (
                  <option key={status} value={status}>
                    {SplitStatusLabels[status]}
                  </option>
                ))}
              </Select>
            </Flex>
          )}
        </Box>

        <TabPanels>
          <TabPanel px={0}>
            <Flex
              justify="center"
              alignItems="center"
              mb={4}
              flexDirection={{ base: "column", md: "row" }}
              width="100%"
              maxWidth="1200px"
              px={{ base: 2, md: 0 }}
            >
              <Flex alignItems="center" mb={{ base: 2, md: 0 }}>
                <IconButton
                  icon={<ChevronLeftIcon />}
                  onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                  isDisabled={page === 1}
                  mr={2}
                  aria-label="Previous Page"
                  size={{ base: "md", md: "sm" }}
                />
                <Text fontSize={{ base: "lg", md: "md" }} fontFamily="Lexend Deca">
                  Página {page} de {totalPages}
                </Text>
                <IconButton
                  icon={<ChevronRightIcon />}
                  onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
                  isDisabled={page === totalPages}
                  ml={2}
                  aria-label="Next Page"
                  size={{ base: "md", md: "sm" }}
                />
              </Flex>
              <Box ml={{ base: 0, md: 4 }} mt={{ base: 2, md: 0 }}>
                <Select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setPage(1);
                  }}
                  width={{ base: "100%", md: "auto" }}
                  size={{ base: "md", md: "sm" }}
                  fontSize={{ base: "lg", md: "md" }}
                  fontFamily="Lexend Deca"
                >
                  <option value={10}>10 por página</option>
                  <option value={20}>20 por página</option>
                  <option value={50}>50 por página</option>
                  <option value={100}>100 por página</option>
                </Select>
              </Box>
            </Flex>
            <Box width="100%" overflow="visible">
              <TableContainer borderRadius="md" boxShadow="md" width="100%">
                <Table size="sm">
                  <Thead
                    {...backgroundStyle}
                    position="sticky"
                    top={0}
                    zIndex={1}
                    boxShadow="md"
                  >
                    <Tr>
                      <Th color={headerColor} textAlign="center" fontSize="xs" py={5}>
                        <HStack justify="center">
                          <Text>AÇÕES</Text>
                        </HStack>
                      </Th>
                      <Th color={headerColor} textAlign="center" fontSize="xs" py={5}>
                        <HStack justify="center">
                          <Text>Id</Text>
                          <IconButton
                            icon={
                              sortBy === "id" && sortDirection === "asc" ? (
                                <ChevronUpIcon />
                              ) : (
                                <ChevronDownIcon />
                              )
                            }
                            variant="ghost"
                            size="1"
                            fontSize={"1rem"}
                            borderRadius={"lg"}
                            aria-label="Sort by Id"
                            bg={"gray.200"}
                            onClick={() => handleSort("id")}
                          />
                        </HStack>
                      </Th>
                      <Th color={headerColor} textAlign="center" fontSize="xs" py={5}>
                        <HStack justify="center">
                          <Text>Nome Fantasia</Text>
                          <IconButton
                            icon={
                              sortBy === "businessName" && sortDirection === "asc" ? (
                                <ChevronUpIcon />
                              ) : (
                                <ChevronDownIcon />
                              )
                            }
                            variant="ghost"
                            size="1"
                            fontSize={"1rem"}
                            borderRadius={"lg"}
                            aria-label="Sort by Business Name"
                            bg={"gray.200"}
                            onClick={() => handleSort("businessName")}
                          />
                        </HStack>
                      </Th>
                      <Th color={headerColor} textAlign="center" fontSize="xs" py={5}>
                        <HStack justify="center">
                          <Text>DOCUMENTO</Text>
                          <IconButton
                            icon={
                              sortBy === "document" && sortDirection === "asc" ? (
                                <ChevronUpIcon />
                              ) : (
                                <ChevronDownIcon />
                              )
                            }
                            variant="ghost"
                            size="1"
                            fontSize={"1rem"}
                            borderRadius={"lg"}
                            aria-label="Sort by Document"
                            bg={"gray.200"}
                            onClick={() => handleSort("document")}
                          />
                        </HStack>
                      </Th>
                      <Th color={headerColor} textAlign="center" fontSize="xs" py={5}>
                        <HStack justify="center">
                          <Text>RAZÃO SOCIAL</Text>
                          <IconButton
                            icon={
                              sortBy === "email" && sortDirection === "asc" ? (
                                <ChevronUpIcon />
                              ) : (
                                <ChevronDownIcon />
                              )
                            }
                            variant="ghost"
                            size="1"
                            fontSize={"1rem"}
                            borderRadius={"lg"}
                            aria-label="Sort by Email"
                            bg={"gray.200"}
                            onClick={() => handleSort("email")}
                          />
                        </HStack>
                      </Th>

                    </Tr>
                  </Thead>
                  <Tbody>
                    {TotalusersData.map(
                      (
                        userItem: CreditPromoter,

                        index: number
                      ) => (
                        <Tr
                          key={index}
                          _hover={{ bg: rowHoverBg }}
                          transition="all 0.3s ease-in-out"
                          borderBottom="1px solid"
                          borderColor={rowBorderColor}
                          fontFamily="Lexend Deca"
                        >
                          <Td
                            textAlign="center"
                            fontSize={{ base: "lg", md: "sm" }}
                            py={{ base: 2, md: 3 }}
                            width={"250px"}
                          >
                            <Flex justify="center" flexDirection="column" gap={2}>
                              <Button
                                size={{ base: "md", md: "md" }}
                                colorScheme="red"
                                onClick={() => handleDeactivateUser}
                                width="100%"
                                py={{ base: 2.5, md: 2 }}
                                fontSize={{ base: "md", md: "sm" }}
                                fontWeight="bold"
                                fontFamily="Lexend Deca"
                              >
                                Desativar Promotora
                              </Button>
                            </Flex>
                          </Td>

                          <Td
                            textAlign="center"
                            fontSize="sm"
                            py={{ base: 2, md: 3 }}
                          >
                            <Link
                              as={RouterLink}
                              to={`/promotoras/${userItem.id}`}
                              color="blue.500"
                            >
                              {userItem.id}
                            </Link>
                          </Td>

                          <Td
                            textAlign="center"
                            fontSize="sm"
                            py={{ base: 2, md: 3 }}
                          >
                            {userItem.businessName}
                          </Td>

                          <Td
                            textAlign="center"
                            fontSize="sm"
                            py={{ base: 2, md: 3 }}
                          >
                            {documentFormatter(userItem.document)}
                          </Td>

                          <Td
                            textAlign="center"
                            fontSize="sm"
                            py={{ base: 2, md: 3 }}
                          >
                            {userItem.legalName}
                          </Td>
                        </Tr>
                      )
                    )}
                  </Tbody>
                </Table>
              </TableContainer>
            </Box>

            <Flex
              justify="center"
              alignItems="center"
              flexDirection={{ base: "column", md: "row" }}
              width="100%"
              maxWidth="1200px"
              px={{ base: 2, md: 0 }}
            >
              <Flex alignItems="center" mt={4} mb={{ base: 2, md: 2 }}>
                <IconButton
                  icon={<ChevronLeftIcon />}
                  onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                  isDisabled={page === 1}
                  mr={2}
                  aria-label="Previous Page"
                  size={{ base: "md", md: "sm" }}
                />
                <Text fontSize={{ base: "lg", md: "md" }} fontFamily="Lexend Deca">
                  Página {page} de {totalPages}
                </Text>
                <IconButton
                  icon={<ChevronRightIcon />}
                  onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
                  isDisabled={page === totalPages}
                  ml={2}
                  aria-label="Next Page"
                  size={{ base: "md", md: "sm" }}
                />
              </Flex>
              <Box ml={{ base: 0, md: 4 }} mb={2} mt={{ base: 2, md: 4 }}>
                <Select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setPage(1);
                  }}
                  width={{ base: "100%", md: "auto" }}
                  size={{ base: "md", md: "sm" }}
                  fontSize={{ base: "lg", md: "md" }}
                  fontFamily="Lexend Deca"
                >
                  <option value={10}>10 por página</option>
                  <option value={20}>20 por página</option>
                  <option value={50}>50 por página</option>
                  <option value={100}>100 por página</option>
                </Select>
              </Box>
            </Flex>
          </TabPanel>

          <TabPanel px={0}>
            <SplitApprovalTab
              searchTerm={splitSearch}
              statusFilter={splitStatusFilter}
            />
          </TabPanel>
        </TabPanels>
      </Box>
    </Tabs>
  );
}
